"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";

const budgetOptions = [
  "Less than $1K",
  "$1K - $3K",
  "$3K - $5K",
  "$5K - $10K",
  "More than $10K",
];

type FormState = "idle" | "submitting" | "success" | "error";

interface Errors {
  name?: string;
  email?: string;
  message?: string;
}

function validate(name: string, email: string, message: string): Errors {
  const errs: Errors = {};
  if (!name.trim()) errs.name = "Name is required.";
  if (!email.trim()) {
    errs.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errs.email = "Enter a valid email address.";
  }
  if (!message.trim()) errs.message = "Tell us a bit about your project.";
  return errs;
}

export default function ContactForm() {
  const [budget, setBudget] = useState("");
  const [fields, setFields] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [formState, setFormState] = useState<FormState>("idle");

  const set = (key: keyof typeof fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFields((f) => ({ ...f, [key]: e.target.value }));
    if (touched[key]) {
      const next = { ...fields, [key]: e.target.value };
      setErrors(validate(next.name, next.email, next.message));
    }
  };

  const blur = (key: string) => () => {
    setTouched((t) => ({ ...t, [key]: true }));
    setErrors(validate(fields.name, fields.email, fields.message));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    const errs = validate(fields.name, fields.email, fields.message);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setFormState("submitting");
    const subject = encodeURIComponent("Project Inquiry");
    const body = encodeURIComponent(
      `Name: ${fields.name}\nEmail: ${fields.email}\nPhone: ${fields.phone}\nBudget: ${budget}\n\nMessage:\n${fields.message}`
    );
    window.location.href = `mailto:thedevcubix@gmail.com?subject=${subject}&body=${body}`;
    setFormState("success");
  };

  if (formState === "success") {
    return (
      <section className="py-12 sm:py-16 md:py-20 bg-surface relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-170 mx-auto px-6 text-center py-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
            className="w-16 h-16 rounded-full bg-red-accent/10 border border-red-accent/30 flex items-center justify-center mx-auto mb-5"
          >
            <svg className="w-8 h-8 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
          <h3 className="text-2xl font-bold font-ui mb-2">Message Sent!</h3>
          <p className="text-foreground/40 text-sm font-body mb-6">We&apos;ll get back to you within 24 hours.</p>
          <button
            onClick={() => {
              setFormState("idle");
              setFields({ name: "", email: "", phone: "", message: "" });
              setBudget("");
              setTouched({});
              setErrors({});
            }}
            className="text-sm text-badge-text underline underline-offset-4 hover:opacity-75 transition-opacity"
          >
            Send another message
          </button>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-surface relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(225,29,36,0.06) 0%, transparent 60%)" }} />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(225,29,36,0.04) 0%, transparent 60%)" }} />

      <div className="max-w-[680px] mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-accent/30 bg-red-accent/5 mb-6">
            <span className="w-2 h-2 rounded-full bg-red-accent animate-pulse" />
            <span className="text-xs font-medium text-badge-text">Start a Project</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.2] font-ui mb-4">
            <span>Let&apos;s Build Something </span>
            <span className="font-serif italic text-red-accent">Great</span>
          </h2>
          <p className="text-foreground/40 text-sm font-body">
            Tell us about your project — we&apos;ll respond within 24 hours.
          </p>
        </motion.div>

        {/* Trust signals */}
        <motion.div
          className="flex items-center justify-center gap-4 sm:gap-6 md:gap-10 mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {[
            { num: "900+", label: "Clients" },
            { num: "5/5", label: "Rating" },
            { num: "24h", label: "Response" },
          ].map((item) => (
            <div key={item.label} className="text-center">
              <p className="text-lg font-bold font-ui text-foreground">{item.num}</p>
              <p className="text-[10px] text-foreground/30 uppercase tracking-wider">{item.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Form */}
        <motion.form
          className="space-y-5"
          onSubmit={handleSubmit}
          noValidate
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                placeholder="Your Name"
                value={fields.name}
                onChange={set("name")}
                onBlur={blur("name")}
                className={`w-full px-4 py-3 sm:px-5 sm:py-4 bg-surface-light border rounded-xl text-sm text-foreground placeholder:text-foreground/25 focus:outline-none transition-colors ${
                  errors.name && touched.name
                    ? "border-red-accent/60 focus:border-red-accent"
                    : "border-card-border focus:border-red-accent/30"
                }`}
              />
              <AnimatePresence>
                {errors.name && touched.name && (
                  <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="text-[11px] text-red-accent mt-1 px-1">
                    {errors.name}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            <div>
              <input
                type="email"
                placeholder="Your Email"
                value={fields.email}
                onChange={set("email")}
                onBlur={blur("email")}
                className={`w-full px-4 py-3 sm:px-5 sm:py-4 bg-surface-light border rounded-xl text-sm text-foreground placeholder:text-foreground/25 focus:outline-none transition-colors ${
                  errors.email && touched.email
                    ? "border-red-accent/60 focus:border-red-accent"
                    : "border-card-border focus:border-red-accent/30"
                }`}
              />
              <AnimatePresence>
                {errors.email && touched.email && (
                  <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="text-[11px] text-red-accent mt-1 px-1">
                    {errors.email}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>

          <input
            type="tel"
            placeholder="WhatsApp Number (optional)"
            value={fields.phone}
            onChange={set("phone")}
            className="w-full px-4 py-3 sm:px-5 sm:py-4 bg-surface-light border border-card-border rounded-xl text-sm text-foreground placeholder:text-foreground/25 focus:outline-none focus:border-red-accent/30 transition-colors"
          />

          <div>
            <p className="text-xs text-foreground/30 uppercase tracking-wider mb-3">Budget Range</p>
            <div className="flex flex-wrap gap-2">
              {budgetOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setBudget(opt)}
                  className={`px-3 sm:px-4 py-3 rounded-lg text-xs font-medium transition-all ${
                    budget === opt
                      ? "bg-red-accent text-white"
                      : "bg-surface-light border border-card-border text-foreground/40 hover:text-foreground/60 hover:border-foreground/15"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <textarea
              placeholder="Tell us about your project..."
              rows={4}
              value={fields.message}
              onChange={set("message")}
              onBlur={blur("message")}
              className={`w-full px-4 py-3 sm:px-5 sm:py-4 bg-surface-light border rounded-xl text-sm text-foreground placeholder:text-foreground/25 focus:outline-none transition-colors resize-none ${
                errors.message && touched.message
                  ? "border-red-accent/60 focus:border-red-accent"
                  : "border-card-border focus:border-red-accent/30"
              }`}
            />
            <AnimatePresence>
              {errors.message && touched.message && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-[11px] text-red-accent mt-1 px-1">
                  {errors.message}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {formState === "error" && (
            <p className="text-xs text-red-accent text-center">
              Something went wrong. Please try WhatsApp or email us directly.
            </p>
          )}

          <button
            type="submit"
            disabled={formState === "submitting"}
            className="w-full py-4 bg-red-accent text-white font-bold rounded-xl text-sm hover:bg-red-accent/90 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {formState === "submitting" ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Sending...
              </>
            ) : (
              <>
                Send Message
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </>
            )}
          </button>
        </motion.form>

        {/* Bottom contact options */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <span className="text-xs text-foreground/20">or reach us directly</span>
          <Link
            href="https://wa.me/+923190708852"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366] text-xs font-medium hover:bg-[#25D366]/15 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </Link>
          <Link
            href="mailto:info@devcubix.com"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-foreground/5 border border-card-border text-foreground/40 text-xs font-medium hover:text-foreground/60 hover:border-foreground/15 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
            info@devcubix.com
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
